import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  autoSetupCharacter,
  claimCharacterOwnership,
  getCharacter,
  getCharacterProfile,
  generateAvatarCandidate,
  uploadAvatar,
  validateFaceReferences,
  saveFaceReferenceImages,
  setAvatar,
  getMinimaxVoices,
  generateVoice,
  bindVoice,
  type DbMinimaxVoice,
  type DbCharacterProfile,
  type FaceReferenceUrls,
  type FaceReferenceValidationResult,
} from '@/api/astraea';
import { spendCredits } from '@/api/auth';
import ProfileSummary from '@/components/ProfileSummary';
import MinimaxVoiceList from '@/components/MinimaxVoiceList';
import { groupVoicesByGender } from '@/lib/voiceGroups';
import { useAskConfirm, usePaidGenerationGuard } from '@/hooks/usePaidGenerationGuard';
import { PAID_GEN_START_CONFIRM } from '@/lib/paidGeneration';

const PLATFORMS: { key: string; label: string }[] = [
  { key: 'xiaohongshu', label: '小红书' },
  { key: 'kuaishou', label: '快手' },
  { key: 'bilibili', label: 'B站' },
  { key: 'wechat_channels', label: '视频号' },
  { key: 'zhihu', label: '知乎' },
  { key: 'wb58', label: '58同城' },
  { key: 'douyin', label: '抖音' },
];

function strList(v: unknown): string[] {
  if (Array.isArray(v)) return v.filter((x) => typeof x === 'string') as string[];
  return [];
}

function friendlyErr(e: unknown, fallback: string): string {
  if (e instanceof TypeError && /fetch|network/i.test(e.message)) return '网络连线失败,请检查网络后重试';
  if (e instanceof Error) return e.message;
  return fallback;
}

/** 压缩大图再上传,避免 base64 过大导致 Failed to fetch */
async function compressImageFile(file: File, maxSide = 1280): Promise<{ dataUrl: string; ext: string }> {
  const raw: string = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result as string);
    fr.onerror = () => reject(new Error('读取档案失败'));
    fr.readAsDataURL(file);
  });
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('图片格式无法识别,请换 JPG/PNG'));
    i.src = raw;
  });
  const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('无法处理图片');
  ctx.drawImage(img, 0, 0, w, h);
  return { dataUrl: canvas.toDataURL('image/jpeg', 0.85), ext: 'jpg' };
}

type FaceSlot = 'front' | 'left_45' | 'right_45';
type FaceUploadState = {
  fileName: string;
  fileSize: number;
  dataUrl: string;
  ext: string;
  width: number;
  height: number;
  zoom: number;
  offsetX: number;
  offsetY: number;
  status: 'ok' | 'warn';
  message: string;
};

const FACE_SLOTS: { key: FaceSlot; title: string; hint: string }[] = [
  { key: 'front', title: '正面半身照', hint: '脸看镜头,五官清楚,看到头肩或上半身' },
  { key: 'left_45', title: '左侧 / 斜左半身照', hint: '往左约 30-60 度,不是和正面重复' },
  { key: 'right_45', title: '右侧 / 斜右半身照', hint: '往右约 30-60 度,不是和正面重复' },
];

function emptyFaceUploads(): Record<FaceSlot, FaceUploadState | null> {
  return { front: null, left_45: null, right_45: null };
}

async function readImageMeta(dataUrl: string): Promise<{ width: number; height: number }> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('图片格式无法识别,请换 JPG/PNG/WebP'));
    i.src = dataUrl;
  });
  return { width: img.width, height: img.height };
}

async function buildFaceUploadState(file: File): Promise<FaceUploadState> {
  const { dataUrl, ext } = await compressImageFile(file, 1600);
  const { width, height } = await readImageMeta(dataUrl);
  const minSide = Math.min(width, height);
  const status = minSide >= 512 ? 'ok' : 'warn';
  return {
    fileName: file.name,
    fileSize: file.size,
    dataUrl,
    ext,
    width,
    height,
    zoom: 1,
    offsetX: 0,
    offsetY: 0,
    status,
    message: status === 'ok' ? '清晰度可用,请确认角度正确' : '解析度偏低,建议换更清楚的半身照',
  };
}

async function cropFaceReference(ref: FaceUploadState): Promise<string> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('图片裁切失败'));
    i.src = ref.dataUrl;
  });
  const canvas = document.createElement('canvas');
  canvas.width = 960;
  canvas.height = 1200;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('无法处理图片');
  ctx.fillStyle = '#f8f5ef';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const baseScale = Math.max(canvas.width / img.width, canvas.height / img.height);
  const scale = baseScale * ref.zoom;
  const w = img.width * scale;
  const h = img.height * scale;
  const x = (canvas.width - w) / 2 + ref.offsetX;
  const y = (canvas.height - h) / 2 + ref.offsetY;
  ctx.drawImage(img, x, y, w, h);
  return canvas.toDataURL('image/jpeg', 0.9);
}

export default function CreatePage() {
  const nav = useNavigate();
  const { askConfirm, confirmDialog } = useAskConfirm();
  const [step, setStep] = useState(1); // 1 名字 2 人设 3 头像 4 声音
  const [err, setErr] = useState<string | null>(null);

  // step 1
  const [name, setName] = useState('');
  const [gender, setGender] = useState<'female' | 'male'>('female');
  const [special, setSpecial] = useState('');
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [setupBusy, setSetupBusy] = useState(false);
  const [characterId, setCharacterId] = useState<number | null>(null);

  // step 2 人设确认
  const [profile, setProfile] = useState<DbCharacterProfile | null>(null);
  const [charDesc, setCharDesc] = useState('');
  const [charBiz, setCharBiz] = useState('');

  // step 3 avatars
  const [avatars, setAvatars] = useState<string[]>([]);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(null);
  const [uploadConsent, setUploadConsent] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [avatarMode, setAvatarMode] = useState<'upload' | 'ai'>('upload');
  const [dragSlot, setDragSlot] = useState<FaceSlot | null>(null);
  const [activeSlot, setActiveSlot] = useState<FaceSlot>('front');
  const [faceUploads, setFaceUploads] = useState<Record<FaceSlot, FaceUploadState | null>>(() => emptyFaceUploads());
  const [faceValidation, setFaceValidation] = useState<FaceReferenceValidationResult | null>(null);
  const [faceValidating, setFaceValidating] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // step 4 voice
  const [voices, setVoices] = useState<DbMinimaxVoice[]>([]);
  const [selVoice, setSelVoice] = useState<string>('');
  const [speed, setSpeed] = useState(1);
  const [pitch, setPitch] = useState(0);
  const [trialText, setTrialText] = useState('你好,我是你的专属角色,今天也要好好加油喔。');
  const [trialUrl, setTrialUrl] = useState<string | null>(null);
  const [trialDoneFor, setTrialDoneFor] = useState<string | null>(null);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [bindBusy, setBindBusy] = useState(false);

  // 等候计时(让用户清楚知道「真的在跑、已等多久」)
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<number | null>(null);
  useEffect(() => {
    const running = setupBusy || avatarBusy;
    if (!running) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setElapsed(0);
      return;
    }
    setElapsed(0);
    timerRef.current = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [setupBusy, avatarBusy]);

  usePaidGenerationGuard({
    isGenerating: setupBusy || avatarBusy || uploadBusy,
    hasPendingResult: step === 3 && (avatars.length > 0 || Object.values(faceUploads).some(Boolean)),
    label: avatarMode === 'upload' ? '三角度角色照片' : '候选头像',
    askConfirm,
  });

  const togglePlatform = (k: string) =>
    setPlatforms((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));

  const genAvatars = useCallback(async (cid: number, charge: boolean) => {
    setErr(null); setAvatarBusy(true); setSelectedAvatar(null); setAvatars([]);
    try {
      if (charge) await spendCredits('avatar', cid);
      const rs = await Promise.all([
        generateAvatarCandidate(cid),
        generateAvatarCandidate(cid),
        generateAvatarCandidate(cid),
      ]);
      const urls = rs.map((r) => r.url).filter((u): u is string => !!u);
      setAvatars(urls);
      if (urls.length === 0) {
        setErr('头像没生成出来,点「重新生成 3 张」再试一次,或改用下方「上传授权照片」。');
      }
    } catch (e) {
      setErr(friendlyErr(e, '头像没生成出来,点「重新生成 3 张」再试一次,或改用「上传照片」。'));
    } finally {
      setAvatarBusy(false);
    }
  }, []);

  // 一键设定 → 建角色 → 认领拥有权 → 取人设 → 进入「人设确认」
  const handleAutoSetup = useCallback(async () => {
    if (!name.trim()) { setErr('请先填角色名字'); return; }
    if (platforms.length === 0) { setErr('请至少选一个平台'); return; }
    setErr(null); setSetupBusy(true);
    try {
      const r = await autoSetupCharacter(name.trim(), platforms, gender, special.trim());
      if (!r.character_id) throw new Error('一键设定失败,请再试一次');
      setCharacterId(r.character_id);
      try { await claimCharacterOwnership(r.character_id); } catch { /* 拥有权认领失败不阻断 */ }
      try {
        const [ch, pf] = await Promise.all([getCharacter(r.character_id), getCharacterProfile(r.character_id)]);
        setCharDesc(ch?.description || '');
        setCharBiz(ch?.business_goal || '');
        setProfile(pf);
      } catch { /* 取人设失败仍可继续 */ }
      setStep(2);
    } catch (e) {
      setErr(e instanceof Error ? e.message : '一键设定失败');
    } finally {
      setSetupBusy(false);
    }
  }, [name, platforms, gender, special]);

  const proceedToAvatars = useCallback(() => {
    if (!characterId) return;
    setErr(null);
    setAvatarMode('upload');
    setStep(3);
  }, [characterId]);

  const handleRegen = async () => {
    if (!characterId) return;
    if (!(await askConfirm(
      '确认重新生成',
      '重新生成 3 张候选头像会扣点，点数不可退回。\n\n确定要再生成一批吗？',
      { confirmLabel: '确定生成', cancelLabel: '取消' },
    ))) return;
    genAvatars(characterId, true);
  };

  const putFaceFile = useCallback(async (slot: FaceSlot, file: File) => {
    if (!uploadConsent) { setErr('请先勾选肖像授权与素材保密同意'); return; }
    if (!file.type.startsWith('image/')) { setErr('请选择图片档案(JPG/PNG/WebP)'); return; }
    try {
      const next = await buildFaceUploadState(file);
      setFaceUploads((prev) => ({ ...prev, [slot]: next }));
      setFaceValidation(null);
      setErr(null);
    } catch (e) {
      setErr(friendlyErr(e, '图片读取失败,请换一张清楚照片'));
    }
  }, [uploadConsent]);

  useEffect(() => {
    if (step !== 3 || avatarMode !== 'upload') return;
    const onPaste = (e: ClipboardEvent) => {
      const file = Array.from(e.clipboardData?.files ?? []).find((f) => f.type.startsWith('image/'));
      if (file) {
        e.preventDefault();
        void putFaceFile(activeSlot, file);
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [activeSlot, avatarMode, putFaceFile, step]);

  const updateFaceAdjust = useCallback((slot: FaceSlot, patch: Partial<Pick<FaceUploadState, 'zoom' | 'offsetX' | 'offsetY'>>) => {
    setFaceUploads((prev) => {
      const current = prev[slot];
      if (!current) return prev;
      return { ...prev, [slot]: { ...current, ...patch } };
    });
    setFaceValidation(null);
  }, []);

  const validateFacePack = useCallback((): string | null => {
    const missing = FACE_SLOTS.find((s) => !faceUploads[s.key]);
    if (missing) return `请先上传「${missing.title}」`;
    const values = FACE_SLOTS.map((s) => faceUploads[s.key]).filter(Boolean) as FaceUploadState[];
    const duplicate = values.some((v, idx) => values.findIndex((x) => x.fileName === v.fileName && x.fileSize === v.fileSize) !== idx);
    if (duplicate) return '三张角度照不能使用同一个档案,请补齐正面/左侧/右侧不同照片';
    const low = values.find((v) => Math.min(v.width, v.height) < 512);
    if (low) return `「${low.fileName}」解析度偏低,请换更清楚的照片`;
    return null;
  }, [faceUploads]);

  const buildCroppedFaceRefs = useCallback(async (): Promise<{ refs: FaceReferenceUrls; dataUrls: FaceReferenceUrls }> => {
    const dataUrls = {} as FaceReferenceUrls;
    for (const slot of ['front', 'left_45', 'right_45'] as FaceSlot[]) {
      const ref = faceUploads[slot];
      if (!ref) throw new Error('请先补齐三张角度照');
      dataUrls[slot] = await cropFaceReference(ref);
    }
    return { refs: dataUrls, dataUrls };
  }, [faceUploads]);

  const handleValidateFacePack = useCallback(async () => {
    if (!characterId) return;
    const invalid = validateFacePack();
    if (invalid) { setErr(invalid); return; }
    setErr(null); setFaceValidating(true); setFaceValidation(null);
    try {
      const { dataUrls } = await buildCroppedFaceRefs();
      const result = await validateFaceReferences(characterId, dataUrls);
      setFaceValidation(result);
      if (!result.ok) setErr(result.message || '照片还不适合锁脸,请依提示重新上传');
    } catch (e) {
      setErr(friendlyErr(e, '照片检查失败,请稍后再试'));
    } finally {
      setFaceValidating(false);
    }
  }, [buildCroppedFaceRefs, characterId, validateFacePack]);

  // 上传三张授权角度照 → front 设为头像 → reference 写入 identity profile → 进选声音
  const handleUploadFacePack = useCallback(async () => {
    if (!characterId) return;
    if (!uploadConsent) { setErr('请先勾选肖像授权与素材保密同意'); return; }
    const invalid = validateFacePack();
    if (invalid) { setErr(invalid); return; }
    if (!faceValidation?.ok || !faceValidation.identity_lock_ready) {
      setErr('请先点「检查三张照片」,通过后才能进入选声音');
      return;
    }
    setErr(null); setUploadBusy(true);
    try {
      const { dataUrls } = await buildCroppedFaceRefs();
      const ordered: FaceSlot[] = ['left_45', 'right_45', 'front'];
      const urls = {} as Record<FaceSlot, string>;
      for (const slot of ordered) {
        const r = await uploadAvatar(characterId, dataUrls[slot], 'jpg');
        if (!r.ok || !r.url) throw new Error(r.message || '上传失败,请重试');
        urls[slot] = r.url;
      }
      await saveFaceReferenceImages(characterId, {
        front: urls.front,
        left_45: urls.left_45,
        right_45: urls.right_45,
      }, faceValidation);
      setVoices(await getMinimaxVoices());
      setStep(4);
    } catch (e) {
      setErr(friendlyErr(e, '上传失败,请重试'));
    } finally {
      setUploadBusy(false);
    }
  }, [buildCroppedFaceRefs, characterId, faceValidation, uploadConsent, validateFacePack]);

  const handlePickAvatar = useCallback(async () => {
    if (!characterId || !selectedAvatar) return;
    setAvatarBusy(true); setErr(null);
    try {
      await setAvatar(characterId, selectedAvatar);
      setVoices(await getMinimaxVoices());
      setStep(4);
    } catch (e) {
      setErr(e instanceof Error ? e.message : '设定头像失败');
    } finally {
      setAvatarBusy(false);
    }
  }, [characterId, selectedAvatar]);

  useEffect(() => {
    if (step !== 4 || !voices.length || selVoice) return;
    const { female, male } = groupVoicesByGender(voices);
    const pick = gender === 'female' ? female[0] : male[0];
    if (pick) setSelVoice(pick.voice_id);
  }, [step, voices, gender, selVoice]);

  const sig = `${selVoice}|${speed}|${pitch}`;
  const trialReady = trialDoneFor === sig && !!trialUrl;
  const canBind = !!selVoice && trialReady;

  const handleTrial = useCallback(async () => {
    if (!characterId || !selVoice) { setErr('请先选一个声音'); return; }
    if (trialText.length > 300) { setErr('试听文案请在 300 字以内'); return; }
    setErr(null); setVoiceBusy(true); setTrialUrl(null);
    try {
      // 试听免费,不扣点
      const r = await generateVoice(characterId, null, selVoice, trialText, speed, pitch);
      if (r.url) { setTrialUrl(r.url); setTrialDoneFor(sig); }
      else setErr(r.message || '试听生成失败,请再试一次');
    } catch (e) {
      setErr(e instanceof Error ? e.message : '试听失败,请再试一次');
    } finally {
      setVoiceBusy(false);
    }
  }, [characterId, selVoice, trialText, speed, pitch, sig]);

  const handleBind = useCallback(async () => {
    if (!characterId || !canBind) return;
    if (!window.confirm('确定用这个声音完成建立角色吗?\n建立后,先前花费的点数不会退回。')) return;
    setBindBusy(true); setErr(null);
    try {
      await bindVoice(characterId, selVoice, speed, pitch);
      nav(`/board/${characterId}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : '绑定失败');
      setBindBusy(false);
    }
  }, [characterId, canBind, selVoice, speed, pitch, nav]);

  const STEPS = ['平台 + 名字', '人设', '头像', '声音'];

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 py-8 md:py-12">
      <div className="flex items-center gap-2 text-xs text-secondary-400 mb-2">
        <Link to="/board" className="hover:text-foreground-600">角色</Link>
        <i className="ri-arrow-right-s-line"></i><span>新建角色</span>
      </div>
      <h1 className="text-2xl font-bold text-foreground-950 mb-1">新建角色</h1>

      <div className="flex items-center gap-2 my-5 flex-wrap">
        {STEPS.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${step >= i + 1 ? 'bg-primary-500 text-background-50' : 'bg-secondary-100 text-secondary-400'}`}>{i + 1}</div>
            <span className={`text-xs ${step >= i + 1 ? 'text-foreground-800' : 'text-secondary-400'}`}>{s}</span>
            {i < STEPS.length - 1 && <div className="w-5 h-px bg-secondary-200"></div>}
          </div>
        ))}
      </div>

      {err && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{err}</div>}

      {/* 等候进度横幅:让用户清楚看到「真的在跑、已等多久」 */}
      {(setupBusy || avatarBusy) && (
        <div className="mb-4 p-4 rounded-xl bg-primary-50 border border-primary-200">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin flex-shrink-0"></div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-primary-800">
                {setupBusy ? 'AI 正在建立人设…' : '正在生成 3 张真人形象…'}
                <span className="ml-2 font-mono text-primary-600">已等候 {elapsed}s</span>
              </div>
              <div className="text-[11px] text-primary-600/80 mt-0.5">
                {setupBusy ? '约需 1 分钟,请先留在此页等它跑完,别关闭或返回。' : '约需 30–60 秒,请先留在此页不要关闭或返回。'}
              </div>
            </div>
          </div>
          <div className="mt-3 h-1.5 w-full rounded-full bg-primary-100 overflow-hidden">
            <div className="h-full bg-primary-400 rounded-full animate-pulse" style={{ width: `${Math.min(95, 12 + elapsed * (setupBusy ? 1.4 : 2.2))}%` }}></div>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-5">
          <div>
            <label className="text-sm font-medium text-foreground-800">角色名字</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="例如:小柔"
              className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-secondary-200 bg-background-100 text-sm focus:outline-none focus:border-primary-400" />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground-800">角色性别</label>
            <p className="text-[11px] text-secondary-400 mt-0.5">选好后,AI 人设、真人形象与声音都只会用这个性别。</p>
            <div className="mt-2 flex gap-2">
              {([['female', '女性'], ['male', '男性']] as const).map(([g, label]) => (
                <button key={g} onClick={() => setGender(g)}
                  className={`px-5 py-2 rounded-full text-sm font-medium cursor-pointer border ${gender === g ? 'bg-primary-500 text-background-50 border-primary-500' : 'border-secondary-200 text-foreground-600 hover:border-secondary-300'}`}>
                  <i className={`${g === 'female' ? 'ri-women-line' : 'ri-men-line'} mr-1`}></i>{label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-foreground-800">要发布在哪些平台(可多选)</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {PLATFORMS.map((p) => (
                <button key={p.key} onClick={() => togglePlatform(p.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer border ${platforms.includes(p.key) ? 'bg-primary-500 text-background-50 border-primary-500' : 'border-secondary-200 text-foreground-600 hover:border-secondary-300'}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-foreground-800">特殊需求(选填)</label>
            <p className="text-[11px] text-secondary-400 mt-0.5">想要的角色设定、背景、卖点、语气、禁忌…直接写,写得简短或口语也没关系,AI 会帮你包装、补完成完整人设。不填也可以。</p>
            <textarea value={special} onChange={(e) => setSpecial(e.target.value)} rows={3} maxLength={1200}
              placeholder="例如:她是离职后做自由接案的设计师,想分享省钱和接案心得,语气轻松但专业,不要太鸡汤"
              className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-secondary-200 bg-background-100 text-sm focus:outline-none focus:border-primary-400 resize-none" />
          </div>
          <div className="rounded-lg bg-primary-50/60 border border-primary-100 p-3 text-xs text-foreground-600">
            <i className="ri-magic-line text-primary-500 mr-1"></i>
            点「一键设定」,AI 会依名字、平台、性别与你的特殊需求自动建好人设、背景、说话方式与边界,完成后会先让你确认,再选头像。
          </div>
          <button onClick={handleAutoSetup} disabled={setupBusy}
            className={`w-full py-3 rounded-full text-sm font-semibold text-background-50 ${setupBusy ? 'bg-primary-400 cursor-wait' : 'bg-primary-500 hover:bg-primary-600'}`}>
            {setupBusy ? `AI 正在建立人设…(已 ${elapsed}s)` : '一键设定'}
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <p className="text-[15px] sm:text-sm text-foreground-600 leading-relaxed">这是 AI 依「{name}」+ 所选平台自动建好的人设,先看看再决定下一步。</p>
          <div className="rounded-xl border border-secondary-200 bg-background-100 p-4 sm:p-5">
            <ProfileSummary character={{ description: charDesc, business_goal: charBiz }} profile={profile} />
            {profile?.content_boundaries && (() => {
              const cb = profile.content_boundaries as Record<string, unknown>;
              const allowed = strList(cb.allowed_topics);
              const restricted = strList(cb.restricted_topics);
              const forbidden = strList(cb.forbidden_claims);
              if (allowed.length + restricted.length + forbidden.length === 0) return null;
              return (
                <div className="py-2.5 border-t border-secondary-100 mt-1">
                  <div className="text-[13px] sm:text-xs font-semibold text-primary-600 mb-1">内容边界</div>
                  {allowed.length > 0 && <div className="text-[15px] sm:text-[13px] text-foreground-700 leading-relaxed">可聊:{allowed.join('、')}</div>}
                  {restricted.length > 0 && <div className="text-[15px] sm:text-[13px] text-amber-700 leading-relaxed">谨慎:{restricted.join('、')}</div>}
                  {forbidden.length > 0 && <div className="text-[15px] sm:text-[13px] text-red-600 leading-relaxed">禁止:{forbidden.join('、')}</div>}
                </div>
              );
            })()}
            {!profile && !charDesc && (
              <div className="text-[15px] sm:text-sm text-secondary-400 py-2">人设已建立(细节稍后可在任务板查看)。</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => nav('/board')} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-secondary-200 text-foreground-600 hover:bg-secondary-50 cursor-pointer">
              先去别的
            </button>
            <button onClick={proceedToAvatars} className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 cursor-pointer">
              看起来不错,下一步:选头像
            </button>
          </div>
          <p className="text-[11px] text-secondary-400">人设已存好,角色已归你。你也可以现在先离开,稍后再回角色列表挑头像。</p>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <div className="flex rounded-lg border border-secondary-200 p-1 bg-background-50">
            <button type="button" onClick={() => setAvatarMode('upload')}
              className={`flex-1 py-2 rounded-md text-xs font-semibold transition-colors cursor-pointer ${avatarMode === 'upload' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:bg-background-100'}`}>
              <i className="ri-upload-2-line mr-1"></i>上传照片
            </button>
            <button type="button" onClick={() => setAvatarMode('ai')}
              className={`flex-1 py-2 rounded-md text-xs font-semibold transition-colors cursor-pointer ${avatarMode === 'ai' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:bg-background-100'}`}>
              <i className="ri-magic-line mr-1"></i>AI 生成
            </button>
          </div>

          {avatarMode === 'upload' ? (
            <div className="space-y-3">
              <p className="text-sm text-foreground-600">上传 3 张授权半身照,系统会用正面、左侧、右侧一起锁定角色五官。你可以点选、拖曳或复制贴上图片。</p>
              <label className="flex items-start gap-2 text-[11px] text-foreground-700 cursor-pointer">
                <input type="checkbox" checked={uploadConsent} onChange={(e) => setUploadConsent(e.target.checked)} className="mt-0.5" />
                <span>我确认已取得本人/肖像授权,同意用于角色形象、换装素材、口播影片生成,并承诺不上传名人、未授权第三方、未成年人或侵权照片。</span>
              </label>
              <div className="rounded-xl bg-primary-50/60 border border-primary-100 p-3 text-[11px] text-primary-800 leading-relaxed">
                为了后续换衣服、换背景仍像同一个人,请分别放入正面、左侧、右侧三张清晰半身照。三张都通过后才可进入选声音。
              </div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) putFaceFile(activeSlot, f); e.currentTarget.value = ''; }} />
              <div className="grid gap-3">
                {FACE_SLOTS.map((slot) => {
                  const ref = faceUploads[slot.key];
                  const active = activeSlot === slot.key;
                  const ai = faceValidation?.[slot.key] as Record<string, unknown> | undefined;
                  return (
                    <div key={slot.key} className={`rounded-xl border p-3 ${active ? 'border-primary-300 bg-primary-50/30' : 'border-secondary-200 bg-background-50'}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-foreground-900">{slot.title}</div>
                          <div className="text-[11px] text-secondary-500">{slot.hint}</div>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${ref ? ref.status === 'ok' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700' : 'bg-secondary-100 text-secondary-500'}`}>
                          {ref ? ref.status === 'ok' ? '可用' : '需更清楚' : '未上传'}
                        </span>
                      </div>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => { setActiveSlot(slot.key); if (uploadConsent && !uploadBusy) fileRef.current?.click(); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { setActiveSlot(slot.key); fileRef.current?.click(); } }}
                        onDragOver={(e) => { e.preventDefault(); setActiveSlot(slot.key); setDragSlot(slot.key); }}
                        onDragLeave={() => setDragSlot(null)}
                        onDrop={(e) => {
                          e.preventDefault(); setDragSlot(null); setActiveSlot(slot.key);
                          const f = e.dataTransfer.files?.[0];
                          if (f) void putFaceFile(slot.key, f);
                        }}
                        className={`mt-3 rounded-lg border-2 border-dashed overflow-hidden ${!uploadConsent ? 'border-secondary-200 bg-secondary-50 cursor-not-allowed' : dragSlot === slot.key ? 'border-primary-400 bg-primary-50' : 'border-secondary-200 bg-background-100 hover:border-primary-300 cursor-pointer'}`}
                      >
                        {ref ? (
                          <div className="grid sm:grid-cols-[150px_1fr] gap-3 p-3">
                            <div className="relative w-full max-w-[180px] aspect-[4/5] mx-auto overflow-hidden rounded-lg bg-secondary-100">
                              <img
                                src={ref.dataUrl}
                                alt={slot.title}
                                className="absolute left-1/2 top-1/2 w-full h-full object-cover"
                                style={{
                                  transform: `translate(calc(-50% + ${ref.offsetX / 8}px), calc(-50% + ${ref.offsetY / 8}px)) scale(${ref.zoom})`,
                                }}
                              />
                              <div className="absolute inset-x-[28%] top-[18%] h-px bg-primary-400/60" />
                              <div className="absolute inset-x-[18%] bottom-[18%] h-px bg-primary-400/40" />
                              <div className="absolute inset-y-0 left-1/2 w-px bg-primary-400/30" />
                            </div>
                            <div className="space-y-2 text-[11px] text-secondary-600">
                              <div>{ref.fileName} · {ref.width}×{ref.height}</div>
                              <div className={ref.status === 'ok' ? 'text-green-700' : 'text-amber-700'}>{ref.message}</div>
                              <label className="block">缩放
                                <input type="range" min={0.8} max={1.8} step={0.05} value={ref.zoom} onChange={(e) => updateFaceAdjust(slot.key, { zoom: parseFloat(e.target.value) })} className="w-full" />
                              </label>
                              <label className="block">左右
                                <input type="range" min={-180} max={180} step={5} value={ref.offsetX} onChange={(e) => updateFaceAdjust(slot.key, { offsetX: parseInt(e.target.value, 10) })} className="w-full" />
                              </label>
                              <label className="block">上下
                                <input type="range" min={-180} max={180} step={5} value={ref.offsetY} onChange={(e) => updateFaceAdjust(slot.key, { offsetY: parseInt(e.target.value, 10) })} className="w-full" />
                              </label>
                              <button type="button" onClick={(e) => { e.stopPropagation(); setActiveSlot(slot.key); fileRef.current?.click(); }} className="text-primary-600 hover:underline">重新选择这张</button>
                            </div>
                          </div>
                        ) : (
                          <div className="p-5 text-center">
                            <i className="ri-image-add-line text-2xl text-primary-500 mb-1 block"></i>
                            <div className="text-xs font-medium text-foreground-800">{uploadConsent ? '点选 / 拖曳 / 复制贴上图片' : '请先勾选上方授权'}</div>
                            <div className="text-[10px] text-secondary-400 mt-1">清晰单人半身照 · JPG/PNG/WebP</div>
                          </div>
                        )}
                      </div>
                      {ai && (
                        <div className={`mt-2 rounded-lg px-2.5 py-2 text-[11px] ${ai.usable === true ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`}>
                          AI 检查:{String(ai.note || (ai.usable === true ? '可用于锁脸' : '需要调整'))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {faceValidation && (
                <div className={`rounded-xl border p-3 text-xs leading-relaxed ${faceValidation.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                  <div className="font-semibold">{faceValidation.ok ? 'AI 检查通过' : 'AI 检查未通过'}</div>
                  <div className="mt-1">{faceValidation.message}</div>
                  {faceValidation.issues && faceValidation.issues.length > 0 && (
                    <ul className="list-disc ml-4 mt-1 space-y-0.5">
                      {faceValidation.issues.slice(0, 3).map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                  )}
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-2">
                <button onClick={handleValidateFacePack} disabled={faceValidating || uploadBusy || !!validateFacePack()}
                  className={`py-3 rounded-full text-sm font-semibold border ${faceValidating || uploadBusy || !!validateFacePack() ? 'border-secondary-200 text-secondary-400 cursor-not-allowed' : 'border-primary-300 text-primary-700 hover:bg-primary-50 cursor-pointer'}`}>
                  {faceValidating ? 'AI 正在检查照片…' : '检查三张照片'}
                </button>
                <button onClick={handleUploadFacePack} disabled={uploadBusy || faceValidating || !faceValidation?.ok}
                  className={`py-3 rounded-full text-sm font-semibold text-background-50 ${uploadBusy || faceValidating || !faceValidation?.ok ? 'bg-primary-300 cursor-not-allowed' : 'bg-primary-500 hover:bg-primary-600 cursor-pointer'}`}>
                  {uploadBusy ? '正在建立角色脸部参考…' : '检查通过,进入选声音'}
                </button>
              </div>
              <ul className="text-[11px] text-secondary-500 list-disc ml-4 space-y-0.5">
                <li>照片不会让角色建立包变便宜;它会让后续换装、换场景更稳定。</li>
                <li>请避免多人照、滤镜照、墨镜口罩、脸太小或角度重复的照片。</li>
              </ul>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-foreground-600">AI 一次生成 3 张候选,挑一张当头像。每张约需 1–2 分钟,请耐心等候。</p>
              {avatarBusy && avatars.length === 0 ? (
                <div className="py-8 text-center text-sm text-secondary-500">
                  <div className="inline-block w-6 h-6 border-2 border-secondary-200 border-t-primary-500 rounded-full animate-spin mb-2"></div>
                  <div>头像生成中…已等候 {elapsed}s</div>
                  <div className="text-[11px] mt-1">你可以切回「上传照片」先完成建立</div>
                </div>
              ) : avatars.length === 0 ? (
                <button onClick={() => characterId && genAvatars(characterId, false)} disabled={avatarBusy}
                  className="w-full py-3 rounded-xl text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer disabled:opacity-50">
                  <i className="ri-magic-line mr-1"></i>开始生成 3 张候选(首次免费)
                </button>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {avatars.map((u) => (
                    <button key={u} onClick={() => setSelectedAvatar(u)}
                      className={`relative rounded-xl overflow-hidden border-2 cursor-pointer ${selectedAvatar === u ? 'border-primary-500' : 'border-secondary-200'}`}>
                      <img src={u} alt="候选头像" className="w-full aspect-square object-cover" />
                      {selectedAvatar === u && <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-xs"><i className="ri-check-line"></i></div>}
                    </button>
                  ))}
                </div>
              )}
              {avatars.length > 0 && (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <button onClick={handleRegen} disabled={avatarBusy}
                    className="px-4 py-2 rounded-lg text-xs font-medium border border-secondary-200 text-foreground-600 hover:bg-secondary-50 cursor-pointer disabled:opacity-50">
                    <i className="ri-refresh-line mr-1"></i>重新生成 3 张(扣点)
                  </button>
                  <button onClick={handlePickAvatar} disabled={!selectedAvatar || avatarBusy}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-semibold text-background-50 ${!selectedAvatar || avatarBusy ? 'bg-primary-300 cursor-not-allowed' : 'bg-primary-500 hover:bg-primary-600 cursor-pointer'}`}>
                    用这张,下一步:选声音
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4">
          <p className="text-sm text-foreground-600">选一个声音,调语速语调,先试听满意了再绑定。系统会依角色性别只显示可用声音,避免男角配女声或女角配男声。</p>
          <div className="text-xs text-secondary-500 inline-flex items-center gap-1.5 bg-secondary-100 rounded-full px-3 py-1">
            <i className={`${gender === 'female' ? 'ri-women-line' : 'ri-men-line'} text-primary-600`}></i>
            角色性别:{gender === 'female' ? '女性' : '男性'} · 只能绑定{gender === 'female' ? '女声' : '男声'}
          </div>
          <MinimaxVoiceList
            voices={voices}
            selectedId={selVoice}
            onSelect={(id) => { setSelVoice(id); setTrialUrl(null); }}
            genderFilter={gender}
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-foreground-700">语速 {speed.toFixed(1)}x</label>
              <input type="range" min={0.5} max={2} step={0.1} value={speed} onChange={(e) => { setSpeed(parseFloat(e.target.value)); setTrialUrl(null); }} className="w-full" />
            </div>
            <div>
              <label className="text-xs text-foreground-700">语调 {pitch}</label>
              <input type="range" min={-12} max={12} step={1} value={pitch} onChange={(e) => { setPitch(parseInt(e.target.value, 10)); setTrialUrl(null); }} className="w-full" />
            </div>
          </div>
          <div>
            <label className="text-xs text-foreground-700">试听文案(≤300 字)</label>
            <textarea value={trialText} maxLength={300} onChange={(e) => { setTrialText(e.target.value); setTrialUrl(null); }} rows={2}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-secondary-200 bg-background-100 text-sm focus:outline-none focus:border-primary-400 resize-none" />
            <div className="text-[10px] text-secondary-400 text-right">{trialText.length}/300</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={handleTrial} disabled={voiceBusy || !selVoice}
              className="px-4 py-2 rounded-lg text-xs font-medium border border-primary-300 text-primary-700 hover:bg-primary-50 cursor-pointer disabled:opacity-50">
              {voiceBusy ? '生成试听中…(约 10 秒)' : '生成试听'}
            </button>
            {selVoice && <span className="text-[11px] text-secondary-500">目前选的:{voices.find((v) => v.voice_id === selVoice)?.name || selVoice}</span>}
          </div>
          {trialUrl && (
            <div className="flex items-center gap-2 rounded-lg bg-primary-50/50 border border-primary-100 px-3 py-2">
              <span className="text-[11px] text-primary-700 whitespace-nowrap"><i className="ri-volume-up-line"></i> 试听:{voices.find((v) => v.voice_id === selVoice)?.name || selVoice}</span>
              <audio src={trialUrl} controls autoPlay className="h-9 flex-1 min-w-0" />
            </div>
          )}
          <button onClick={handleBind} disabled={!canBind || bindBusy}
            className={`w-full py-3 rounded-full text-sm font-semibold text-background-50 ${!canBind || bindBusy ? 'bg-primary-300 cursor-not-allowed' : 'bg-primary-500 hover:bg-primary-600 cursor-pointer'}`}>
            {bindBusy ? '绑定中…' : canBind ? '绑定声音,完成建立' : '请先选声音并试听,才能绑定'}
          </button>
        </div>
      )}
      {confirmDialog}
    </div>
  );
}
